import { Search } from 'lucide-react';
import { useDeferredValue, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useSession } from '@/features/auth/session-context';
import { useGlobalSearch } from '@/features/shell/api';
import { PatientIdentityText } from '@/pages/patients/PatientIdentityText';
import styles from './Shell.module.css';

/**
 * "Search patient, IC, claim…": one search for every role; results show only
 * what the active role may see (patients, lab orders, bills; claims later).
 */
/** RL-1667 (gap #137): plain bill statuses in search, not "open" / "closed". */
const BILL_STATUS_TEXT: Record<string, string> = {
  open: 'Unpaid',
  closed: 'Paid',
  void: 'Void',
};
const billStatusText = (status: string) =>
  BILL_STATUS_TEXT[status] ?? status.charAt(0).toUpperCase() + status.slice(1);

export function GlobalSearch() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useSession();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const deferred = useDeferredValue(query);
  const { data, isFetching } = useGlobalSearch(deferred);
  const listId = useId();
  const box = useRef<HTMLDivElement>(null);
  const term = query.trim();

  const go = (href: string) => {
    setOpen(false);
    setQuery('');
    navigate(href);
  };

  const sections = data
    ? [
        data.patients && {
          key: 'patients',
          label: 'Patients',
          rows: data.patients.map((p) => ({
            id: p.id,
            href: p.href,
            title: p.fullName,
            sub: <PatientIdentityText identity={p.identity} />,
          })),
        },
        data.labOrders && {
          key: 'lab',
          label: 'Lab orders',
          rows: data.labOrders.map((o) => ({
            id: o.id,
            href: o.href,
            title: `${o.number ?? ''} · ${o.patientName}`,
            sub: o.tests.join(', '),
          })),
        },
        data.bills && {
          key: 'bills',
          label: 'Bills',
          rows: data.bills.map((b) => ({
            id: b.id,
            href: b.href,
            title: `${b.number} · ${b.patientName}`,
            sub: billStatusText(b.status),
          })),
        },
      ].filter(Boolean as unknown as <T>(x: T | null) => x is T)
    : [];
  const nothingToSearch = data && sections.length === 0;
  const empty = data && sections.every((s) => s.rows.length === 0);

  return (
    <div
      ref={box}
      className={styles.searchBox}
      onBlur={(e) => {
        if (!box.current?.contains(e.relatedTarget as Node)) setOpen(false);
      }}
    >
      <form
        className={styles.search}
        role="search"
        onSubmit={(e) => {
          e.preventDefault();
          if (term && hasPermission('patients:read'))
            go(`/patients?q=${encodeURIComponent(term)}`);
        }}
      >
        <Search size={15} aria-hidden="true" />
        <input
          className={styles.searchInput}
          aria-label={t('shell.search')}
          placeholder={t('shell.search')}
          value={query}
          role="combobox"
          aria-expanded={open && term.length >= 2}
          aria-controls={listId}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setOpen(false);
          }}
        />
      </form>
      {open && term.length >= 2 && (
        <div
          id={listId}
          className={styles.searchResults}
          role="listbox"
          aria-label="Search results"
          aria-busy={isFetching}
        >
          {!data && <p className={styles.searchNote}>Searching…</p>}
          {nothingToSearch && (
            <p className={styles.searchNote}>
              Search is not available for your role yet.
            </p>
          )}
          {data && !nothingToSearch && empty && (
            <p className={styles.searchNote}>Nothing found for “{term}”.</p>
          )}
          {sections.map(
            (s) =>
              s.rows.length > 0 && (
                <div key={s.key} className={styles.searchSection}>
                  <p className="eyebrow">{s.label}</p>
                  {s.rows.map((r) => (
                    <button
                      key={r.id}
                      type="button"
                      role="option"
                      aria-selected={false}
                      className={styles.searchRow}
                      onClick={() => go(r.href)}
                    >
                      <span className={styles.searchTitle}>{r.title}</span>
                      <span className={styles.searchSub}>{r.sub}</span>
                    </button>
                  ))}
                </div>
              ),
          )}
        </div>
      )}
    </div>
  );
}
